import { useEffect, useState } from "react";
import BathtubIcon from "@assets/svg/Bath.svg?react";
import BedroomIcon from "@assets/svg/Bed.svg?react";
import UserIcon from "@assets/svg/UserIcon.svg?react";
import LineIcon from "@assets/svg/LineIcon.svg?react";
import useBreakPoinst from "@hooks/useBreakPoinst";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import {
  PaymentPageShell,
  PaymentPageLogo,
  DetailsContainer,
  ImageContainer,
  ContentContainer,
  MainTitle,
  FeaturesContainer,
  FeatureItem,
  DateContainer,
  DateItem,
  DateLabel,
  DateValue,
  DateSubtext,
  LineIconContainer,
  AgreementBlockedBanner,
} from "./styles";
import { Logo } from "../../utils/image-consts";
import { useWelcomeBook, useRentalAgreement } from "@services";
import { Navigate, useParams } from "react-router-dom";
import LoadingWrapper from "../../components/LoadingWrapper";
import InvoiceBreakdown from "./InvoiceBreakdown";
import RentalAgreementRow from "./RentalAgreementRow";
import RentalAgreementModal from "./RentalAgreementModal";
import PaymentSection from "./PaymentSection";

/**
 * MS-533 — the single payment page reached from the "complete your payment" email.
 *
 * Merges what used to be GuestPortalSummary + GuestPortalCheckout. When the host requires the
 * rental agreement the payment section stays disabled until the guest signs; the backend
 * enforces the same rule independently.
 */
const GuestPortalSummary = () => {
  const { isPhone } = useBreakPoinst();
  const { id } = useParams();
  const reservationId = id ?? "";

  const {
    GetWelcomeBookQuery,
    SaveReservationTransactionLogMutation,
    savePayment,
  } = useWelcomeBook();
  const { data } = GetWelcomeBookQuery(reservationId);
  const result = data?.data?.result;

  const [modalOpen, setModalOpen] = useState(false);
  const [paymentDescription, setPaymentDescription] = useState<string>("");

  const agreementRequired = !!result?.isRentalAgreementRequired;
  const agreementAccepted = !!result?.isRentalAgreementAccepted;
  const paymentBlocked = agreementRequired && !agreementAccepted;

  const {
    document: agreement,
    isLoading,
    loadError,
    accept,
    isAccepting,
  } = useRentalAgreement(reservationId, modalOpen);

  const { mutate: createPaymentDescription, isPending } =
    SaveReservationTransactionLogMutation();

  // The Moyasar form needs a payment description, which the backend only issues once the gate is
  // satisfied. Guarding on `result` is load-bearing: before the welcome book resolves,
  // paymentBlocked is false because result is undefined, so an unguarded effect would fire a
  // guaranteed-400 request on every gated reservation and mint a transaction log for reservations
  // that are already paid.
  //
  // `loadError` is not part of this guard, and that is intentional: the rental-agreement query
  // only runs while the modal is open, which requires `agreementRequired` to already be true and
  // `result` to already be loaded (the row that opens the modal only renders once both hold). A
  // guest can only reach a `loadError` state while still unsigned, i.e. while `paymentBlocked` is
  // already true and this effect is already a no-op; by the time acceptance succeeds and
  // `paymentBlocked` flips false, `paymentDescription` has already been minted (or the effect is
  // about to mint it) and any later, unrelated fetch error from reopening the modal cannot make
  // this effect re-fire.
  useEffect(() => {
    if (!result || paymentBlocked || paymentDescription) return;
    if (result.paymentStatus === "Passed" || result.shouldUserPay === false)
      return;

    createPaymentDescription(reservationId, {
      onSuccess: (response: any) =>
        setPaymentDescription(response?.data?.result ?? ""),
    });
  }, [result, reservationId, paymentBlocked, paymentDescription]);

  dayjs.locale("ar");
  const formatArabicDate = (dateString: string) =>
    dateString ? dayjs(dateString).format("D MMMM") : "";

  if (result?.shouldUserPay === false) {
    return <Navigate to={`/guest/${reservationId}`} />;
  }

  return (
    <LoadingWrapper
      style={{ zIndex: 1111 }}
      loading={isPending && !paymentDescription}
    >
      <PaymentPageShell $isPhone={isPhone}>
        <PaymentPageLogo $isPhone={isPhone}>
          <img src={Logo} alt="مسؤول" />
        </PaymentPageLogo>

        <DetailsContainer>
          <ImageContainer $isPhone={isPhone}>
            <img src={result?.image?.[0]} alt="property" />
          </ImageContainer>

          <ContentContainer $isPhone={isPhone}>
            <MainTitle $isPhone={isPhone}>{result?.propertyName}</MainTitle>
            {/* MS-541 — the booking reference. dir="ltr" and nowrap keep the Latin code from
                wrapping or reordering inside this RTL page; it has to be readable aloud verbatim
                and match the confirmation email and the host's reservation list exactly. */}
            {result?.bookingCode && (
              <DateLabel
                $isPhone={isPhone}
                dir="ltr"
                style={{ whiteSpace: "nowrap", alignSelf: "flex-start" }}
              >
                رقم الحجز: {result.bookingCode}
              </DateLabel>
            )}
            <FeaturesContainer $isPhone={isPhone}>
              <FeatureItem $isPhone={isPhone}>
                <BathtubIcon />
                {result?.bathroomCount} حمامات
              </FeatureItem>
              <FeatureItem $isPhone={isPhone}>
                <BedroomIcon />
                {result?.bedCount} أسرّة
              </FeatureItem>
              <FeatureItem $isPhone={isPhone}>
                <UserIcon />
                {result?.guestCount} ضيوف
              </FeatureItem>
            </FeaturesContainer>
          </ContentContainer>

          <DateContainer $isPhone={isPhone}>
            <DateItem $isPhone={isPhone}>
              <DateLabel $isPhone={isPhone}>تسجيل الدخول</DateLabel>
              <DateValue $isPhone={isPhone}>
                {formatArabicDate(result?.checkInDate)}
              </DateValue>
              <DateSubtext $isPhone={isPhone}>
                {result?.checkInDate}
              </DateSubtext>
              {/* MS-533: hostCheckInTimeText — NOT checkInTime, which silently falls back to
                  03:00 PM and so cannot express "the host set nothing". Absent means the line is
                  omitted rather than stating a time the host never chose. */}
              {result?.hostCheckInTimeText && (
                <DateSubtext $isPhone={isPhone}>
                  من الساعة {result.hostCheckInTimeText}
                </DateSubtext>
              )}
            </DateItem>
            <LineIconContainer>
              {/* MS-533: width is driven by the flex container, not a fixed 200/80 —
                  a hard width here is what squeezed the date columns into wrapping. */}
              <LineIcon width="100%" />
            </LineIconContainer>
            <DateItem $isPhone={isPhone}>
              <DateLabel $isPhone={isPhone}>تسجيل الخروج</DateLabel>
              <DateValue $isPhone={isPhone}>
                {formatArabicDate(result?.checkOutDate)}
              </DateValue>
              <DateSubtext $isPhone={isPhone}>
                {result?.checkOutDate}
              </DateSubtext>
              {/* See the check-in note above — same rule, same field family. */}
              {result?.hostCheckOutTimeText && (
                <DateSubtext $isPhone={isPhone}>
                  قبل الساعة {result.hostCheckOutTimeText}
                </DateSubtext>
              )}
            </DateItem>
          </DateContainer>

          {/* MS-605: the whole split, straight from the welcome book. Passing only totalPrice
              and the night count is what forced this page to divide, and what printed the
              VAT-inclusive figure as the host's nightly rate. */}
          <InvoiceBreakdown
            nightlyRate={result?.nightlyRate}
            numberOfNights={result?.numberOfNights}
            subtotal={result?.subtotal}
            vatAmount={result?.vatAmount}
            vatRatePercent={result?.vatRatePercent}
            guestTotal={result?.guestTotal}
            totalPrice={result?.totalPrice}
            isPhone={isPhone}
          />

          {agreementRequired && (
            <RentalAgreementRow
              accepted={agreementAccepted}
              isPhone={isPhone}
              onOpen={() => setModalOpen(true)}
            />
          )}

          {result?.paymentStatus !== "Passed" && (
            <PaymentSection
              reservationId={reservationId}
              propertyName={result?.propertyName ?? ""}
              totalPrice={result?.totalPrice ?? 0}
              publicKey={result?.publicKey ?? ""}
              paymentDescription={paymentDescription}
              // `!!loadError` is deliberately redundant with `paymentBlocked` today: the modal hides
              // its accept button whenever loadError is set (RentalAgreementModal.tsx), so
              // agreementAccepted can never flip true during a terminal failure and paymentBlocked
              // stays true on its own. That coupling lives in a different component and isn't
              // enforced by a test, so it is spelled out here explicitly rather than relied on
              // implicitly — a future change to when the modal can open, or to that button gating,
              // must not silently re-enable payment during a terminal render failure.
              disabled={paymentBlocked || !!loadError || !paymentDescription}
              onCompleted={async (payment: any) => {
                await savePayment(payment);
              }}
            />
          )}

          {paymentBlocked && (
            <AgreementBlockedBanner>
              وافق على اتفاقية الإيجار لتفعيل الدفع
            </AgreementBlockedBanner>
          )}

          <RentalAgreementModal
            open={modalOpen}
            agreement={agreement}
            isLoading={isLoading}
            loadError={loadError}
            isSubmitting={isAccepting}
            onAccept={() =>
              accept(undefined, { onSuccess: () => setModalOpen(false) })
            }
            onClose={() => setModalOpen(false)}
          />
        </DetailsContainer>
      </PaymentPageShell>
    </LoadingWrapper>
  );
};

export default GuestPortalSummary;
